import React, { lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/auth';
import { ModelRegistryProvider } from './contexts/ModelRegistryContext';

import { RoadmapProvider } from './contexts/roadmap';
import { ServerStatusProvider } from './contexts/ServerStatusContext';
import { UserflowProvider } from './contexts/UserflowProvider';
import { WidgetProvider } from './contexts/WidgetContext';
import { DashboardProvider } from './contexts/DashboardContext';

import NavigationHeader from './components/common/NavigationHeader';
import { GlobalToastRenderers } from './components/common/GlobalToastRenderers';
// Admin/authenticated pages are lazy-loaded below to keep them out of the public bundle.
import OAuthCallback from './components/social/OAuthCallback';
import NotFound from './pages/NotFound';
import './App.css';
import { CourseNavigationProvider } from './contexts/CourseNavigationContext';
import { CampaignDrawerProvider } from './contexts/CampaignDrawerContext';
import { RequireAdminRoute } from './components/auth/RequireAdminRoute';

const RequireActiveSeat = lazy(() => import('./components/auth/RequireActiveSeat'));
const TwoColumnLayout = lazy(() => import('./components/layout/TwoColumnLayout').then((module) => ({ default: module.TwoColumnLayout })));
const CompanyProvider = lazy(() => import('./contexts/company/CompanyContext').then((module) => ({ default: module.CompanyProvider })));
const RequireCompanyAdmin = lazy(() => import('./components/auth/RequireCompanyAdmin').then((module) => ({ default: module.RequireCompanyAdmin })));
const CompanyConsoleLayout = lazy(() => import('./components/company/CompanyConsoleLayout').then((module) => ({ default: module.CompanyConsoleLayout })));
const AdminShell = lazy(() => import('./components/admin/layout/AdminShell'));
import { UnsavedChangesProvider } from '@/components/admin/unsavedChanges';
const Index = lazy(() => import('./pages/Index'));
const RoadmapsPage = lazy(() => import('./pages/RoadmapsPage'));
const RoadmapPage = lazy(() => import('./pages/RoadmapPage'));
const StandaloneAuthPage = lazy(() => import('./pages/StandaloneAuthPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const CoursesOverviewPage = lazy(() => import('./pages/CoursesOverviewPage'));
const CourseDetailPage = lazy(() => import('./pages/CourseDetailPage').then((module) => ({ default: module.CourseDetailPage })));
const WorkspaceLaunchPage = lazy(() => import('@/pages/WorkspaceLaunchPage').then((module) => ({ default: module.WorkspaceLaunchPage })));
const PaymentSuccessPage = lazy(() => import('@/pages/PaymentSuccessPage').then((module) => ({ default: module.PaymentSuccessPage })));
const PaymentReturnPage = lazy(() => import('@/pages/PaymentReturnPage').then((module) => ({ default: module.PaymentReturnPage })));
const PaymentCancelPage = lazy(() => import('@/pages/PaymentCancelPage').then((module) => ({ default: module.PaymentCancelPage })));
const WidgetDemoPage = lazy(() => import('@/pages/WidgetDemoPage'));
const CampaignDrawer = lazy(() => import('./components/campaign/CampaignDrawer'));
const UserInstanceDebug = lazy(() => import('./components/debug/UserInstanceDebug').then((module) => ({ default: module.UserInstanceDebug })));
const DirectBrandInstanceCheck = lazy(() => import('./components/debug/DirectBrandInstanceCheck').then((module) => ({ default: module.DirectBrandInstanceCheck })));

// Lazy load new pages with @/ alias
const RoadmapInstancesPage = lazy(() => import('@/pages/RoadmapInstancesPage'));
const RoadmapInstancePage = lazy(() => import('@/pages/RoadmapInstancePage'));
const LessonViewerPage = lazy(() => import('@/pages/LessonViewerPage'));

const InstancesPage = lazy(() => import('@/pages/InstancesPage'));
const ToolsAppendixPage = lazy(() => import('@/pages/ToolsAppendixPage'));
const KnowledgeBase = lazy(() => import('@/pages/KnowledgeBase'));
const KnowledgeBaseCategory = lazy(() => import('@/pages/KnowledgeBaseCategory'));
const KnowledgeBaseSearch = lazy(() => import('@/pages/KnowledgeBaseSearch'));
const KnowledgeBaseArticle = lazy(() => import('@/pages/KnowledgeBaseArticle'));
const KbArticleEditor = lazy(() => import('@/pages/admin/KbArticleEditor'));
const KnowledgeBaseDashboard = lazy(() => import('@/pages/admin/KnowledgeBaseDashboard'));
const LTISetupPage = lazy(() => import('@/pages/admin/LTISetupPage'));
const LTILoginPage = lazy(() => import('@/pages/LTILoginPage'));
const LTIDeepLinkPage = lazy(() => import('@/pages/LTIDeepLinkPage'));
const PublicCourseViewPage = lazy(() => import('@/pages/PublicCourseViewPage'));
const PublicPortfolioPage = lazy(() => import('@/pages/PublicPortfolioPage'));
const EducatePublicPage = lazy(() => import('@/pages/EducatePublicPage'));

const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const CertificatesPage = lazy(() => import('@/pages/CertificatesPage'));
const StudentsPage = lazy(() => import('@/pages/StudentsPage'));
const AdminStudentProfilePage = lazy(() => import('@/pages/admin/AdminStudentProfilePage'));
const CampaignHubPage = lazy(() => import('@/pages/CampaignHubPage'));
const SetPasswordPage = lazy(() => import('@/pages/SetPasswordPage'));
const SurveyPage = lazy(() => import('@/pages/SurveyPage'));
const SurveyResultsPage = lazy(() => import('@/pages/admin/SurveyResultsPage'));
const CredentialsDashboardPage = lazy(() => import('@/pages/admin/CredentialsDashboardPage'));
const CredentialDataQualityPage = lazy(() => import('@/pages/admin/CredentialDataQualityPage'));
const CompanyDashboardPage = lazy(() => import('@/pages/admin/CompanyDashboardPage'));
const AssessmentsPage = lazy(() => import('@/pages/admin/AssessmentsPage'));
const BadgesPage = lazy(() => import('@/pages/admin/BadgesPage'));
const PathwayGroupsPage = lazy(() => import('@/pages/admin/PathwayGroupsPage'));
const PathwaysPage = lazy(() => import('@/pages/admin/PathwaysPage'));
const PathwayEditorPage = lazy(() => import('@/pages/admin/PathwayEditorPage'));
const PathwayProgressPage = lazy(() => import('@/pages/admin/PathwayProgressPage'));
const AssessmentEditorPage = lazy(() => import('@/pages/admin/AssessmentEditorPage'));
const AssessmentSubmissionsPage = lazy(() => import('@/pages/admin/AssessmentSubmissionsPage'));
const RegistrationFormsPage = lazy(() => import('@/pages/admin/RegistrationFormsPage'));
const RegistrationFormEditorPage = lazy(() => import('@/pages/admin/RegistrationFormEditorPage'));
const RegistrationPublicPage = lazy(() => import('@/pages/RegistrationPublicPage'));
const AssessmentSubmissionReportPage = lazy(() => import('@/pages/admin/AssessmentSubmissionReportPage'));
const AssessmentReviewAdminPage = lazy(() => import('@/pages/admin/AssessmentReviewAdminPage'));
const AssessmentPublicPage = lazy(() => import('@/pages/AssessmentPublicPage'));
const AssessmentAssignmentPage = lazy(() => import('@/pages/AssessmentAssignmentPage'));
const AssessmentResultPage = lazy(() => import('@/pages/AssessmentResultPage'));
const AssessmentReviewQueuePage = lazy(() => import('@/pages/review/AssessmentReviewQueuePage'));
const PublicBadgePage = lazy(() => import('@/pages/PublicBadgePage'));
const BadgeVerifyPage = lazy(() => import('@/pages/BadgeVerifyPage'));
const PublicBadgeProfilePage = lazy(() => import('@/pages/PublicBadgeProfilePage'));

// Admin / authenticated-only pages — lazy so they're not in the public initial bundle.
const PlannerCalendarPage = lazy(() => import('./pages/PlannerCalendarPage'));
const AdminRoadmapsPage = lazy(() => import('./pages/admin/AdminRoadmapsPage'));
const CreateRoadmapPage = lazy(() => import('./pages/admin/CreateRoadmapPage'));
const EditRoadmapPage = lazy(() => import('./pages/admin/EditRoadmapPage'));
const CourseEnrollmentsPage = lazy(() => import('./pages/admin/CourseEnrollmentsPage'));
const GroupManagementPage = lazy(() => import('./pages/admin/GroupManagementPage'));
const AdminFeedPage = lazy(() => import('./pages/admin/AdminFeedPage'));
const TestQuery = lazy(() => import('./pages/admin/TestQuery'));
const AdminAssetsPage = lazy(() => import('./pages/admin/AdminAssetsPage'));
const AdminToolsManagementPage = lazy(() => import('./pages/admin/AdminToolsManagementPage'));
const DesignSystemPreviewPage = lazy(() => import('./pages/admin/DesignSystemPreviewPage'));
const ChatSettingsPage = lazy(() => import('./pages/admin/ChatSettingsPage'));
const CompanyManagementPage = lazy(() => import('./pages/admin/CompanyManagementPage'));
const CompanySeatRosterPage = lazy(() => import('@/pages/company/CompanySeatRosterPage'));
const CompanyStudentAssignmentPage = lazy(() => import('@/pages/company/CompanyStudentAssignmentPage'));
const CompanyWaitlistPage = lazy(() => import('@/pages/company/CompanyWaitlistPage'));
const CompanyInstructorsPage = lazy(() => import('@/pages/company/CompanyInstructorsPage'));
const CompanyAdminsPage = lazy(() => import('@/pages/company/CompanyAdminsPage'));
const CompanySettingsPage = lazy(() => import('@/pages/company/CompanySettingsPage'));
const CompanyInstructorViewPage = lazy(() => import('@/pages/company/CompanyInstructorViewPage'));
const CompanyReviewersPage = lazy(() => import('@/pages/company/CompanyReviewersPage'));
const StudentOverviewPage = lazy(() => import('./components/admin/StudentOverviewPage'));
const InstructorCoursePage = lazy(() => import('@/pages/InstructorCoursePage'));
const StudentInstancesForInstructorPage = lazy(() => import('./pages/StudentInstancesForInstructorPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

// Layout wrapper for pages that need NavigationHeader
const PublicLayoutWithHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-background">
    <NavigationHeader />
    {children}
  </div>
);

// Import universal chat context for admin layout. The chat UI itself is lazy so
// it does not inflate the public startup bundle.
import { GlobalChatProvider } from './contexts/GlobalChatContext';
const UniversalChat = lazy(() => import('./components/ai').then((module) => ({ default: module.UniversalChat })));

const AdminLayoutWithNav: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <RequireAdminRoute>
    <GlobalChatProvider>
      <UnsavedChangesProvider>
        <AdminShell>{children}</AdminShell>
        {/* Universal AI Chat - available on admin pages */}
        <UniversalChat />
      </UnsavedChangesProvider>
    </GlobalChatProvider>
  </RequireAdminRoute>
);

const MicroLearnSlugRedirect: React.FC = () => {
  const { slug } = useParams();
  return <Navigate to={`/knowledge-base/${slug}`} replace />;
};

const MicroLearnAdminPostRedirect: React.FC = () => {
  const { id } = useParams();
  return <Navigate to={`/admin/knowledge-base/article/${id}`} replace />;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ModelRegistryProvider>
        <WidgetProvider>
        <AuthProvider>
          <RoadmapProvider>
            <ServerStatusProvider>
              <UserflowProvider>
              <Router
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true
                }}
              >
                <div className="min-h-screen bg-background">
                  <React.Suspense fallback={null}>
                    <Routes>
                      {/* Widget content route - redirects to lessons */}
                      <Route path="/widget-content" element={<Navigate to="/lessons" replace />} />

                      {/* Public routes with header for navigation */}
                      <Route path="/" element={<PublicLayoutWithHeader><Index /></PublicLayoutWithHeader>} />
                      <Route path="/login" element={<StandaloneAuthPage />} />
                      <Route path="/public-login" element={<Navigate to="/login" replace />} />
                      <Route path="/signup" element={<Navigate to="/login?tab=signup" replace />} />
                      <Route path="/forgot-password" element={<PublicLayoutWithHeader><ForgotPasswordPage /></PublicLayoutWithHeader>} />
                      <Route path="/reset-password" element={<PublicLayoutWithHeader><ResetPasswordPage /></PublicLayoutWithHeader>} />
                      <Route path="/oauth/callback" element={<OAuthCallback />} />

                      <Route path="/set-password" element={<SetPasswordPage />} />

                      {/* LTI routes */}
                      <Route path="/lti/login" element={<LTILoginPage />} />
                      <Route path="/lti/deep-link" element={<LTIDeepLinkPage />} />

                      {/* Public course view - noindex page for sharing course content */}
                      <Route path="/course-view/:courseId" element={<PublicCourseViewPage />} />
                      <Route path="/educate" element={<EducatePublicPage />} />

                      {/* Public portfolio page - shareable student resume */}
                      <Route path="/p/:slug" element={<PublicPortfolioPage />} />

                      {/* Campaign hub - public view + owner edit mode */}
                      <Route path="/hub/:slug" element={<GlobalChatProvider><CampaignHubPage /><UniversalChat /></GlobalChatProvider>} />

                      {/* Public survey - no auth required */}
                      <Route path="/survey/:token" element={<SurveyPage />} />
                      <Route path="/assessments/:slug" element={<AssessmentPublicPage />} />
                      <Route path="/enroll/:slug" element={<RegistrationPublicPage />} />
                      <Route path="/r/:slug" element={<RegistrationPublicPage />} />
                      <Route path="/a/:token" element={<AssessmentAssignmentPage />} />
                      <Route path="/assessment-results/:token" element={<AssessmentResultPage />} />
                      <Route path="/review/assessments" element={<AssessmentReviewQueuePage />} />
                      <Route path="/badges/verify" element={<BadgeVerifyPage />} />
                      <Route path="/badges/:id" element={<PublicBadgePage />} />
                      <Route path="/badge-profiles/:slug" element={<PublicBadgeProfilePage />} />

                      {/* Admin routes with navigation */}
                      <Route path="/admin/login" element={<Navigate to="/login?redirect=/admin/roadmaps" replace />} />
                      <Route path="/admin/signup" element={<Navigate to="/login" replace />} />
                      <Route path="/admin/roadmaps" element={<AdminLayoutWithNav><AdminRoadmapsPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/roadmaps/create" element={<AdminLayoutWithNav><CreateRoadmapPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/roadmaps/:templateId/edit" element={<AdminLayoutWithNav><EditRoadmapPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/course-enrollments" element={<AdminLayoutWithNav><CourseEnrollmentsPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/course-enrollments/:courseId" element={<AdminLayoutWithNav><CourseEnrollmentsPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/course-enrollments/:courseId/:tab" element={<AdminLayoutWithNav><CourseEnrollmentsPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/groups" element={<AdminLayoutWithNav><GroupManagementPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/groups/:groupId" element={<AdminLayoutWithNav><GroupManagementPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/groups/:groupId/:tab" element={<AdminLayoutWithNav><GroupManagementPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/feed" element={<AdminLayoutWithNav><AdminFeedPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/assets" element={<AdminLayoutWithNav><AdminAssetsPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/test-query" element={<AdminLayoutWithNav><TestQuery /></AdminLayoutWithNav>} />

                      <Route path="/admin/companies" element={<AdminLayoutWithNav><CompanyManagementPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/companies/:companyId" element={<AdminLayoutWithNav><CompanyManagementPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/companies/:companyId/:tab" element={<AdminLayoutWithNav><CompanyManagementPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/company-dashboard" element={<AdminLayoutWithNav><CompanyDashboardPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/students" element={<AdminLayoutWithNav><StudentOverviewPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/students/:userId" element={<AdminLayoutWithNav><AdminStudentProfilePage /></AdminLayoutWithNav>} />
                      <Route path="/admin/students/:userId/:tab" element={<AdminLayoutWithNav><AdminStudentProfilePage /></AdminLayoutWithNav>} />
                      <Route path="/admin/tools" element={<AdminLayoutWithNav><AdminToolsManagementPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/design-system" element={<AdminLayoutWithNav><DesignSystemPreviewPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/chat-settings" element={<AdminLayoutWithNav><ChatSettingsPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/micro-learn" element={<Navigate to="/admin/knowledge-base" replace />} />
                      <Route path="/admin/micro-learn/post/new" element={<Navigate to="/admin/knowledge-base/article/new" replace />} />
                      <Route path="/admin/micro-learn/post/:id" element={<MicroLearnAdminPostRedirect />} />
                      <Route path="/admin/knowledge-base" element={<AdminLayoutWithNav><KnowledgeBaseDashboard /></AdminLayoutWithNav>} />
                      <Route path="/admin/knowledge-base/article/new" element={<AdminLayoutWithNav><KbArticleEditor /></AdminLayoutWithNav>} />
                      <Route path="/admin/knowledge-base/article/:id" element={<AdminLayoutWithNav><KbArticleEditor /></AdminLayoutWithNav>} />
                      <Route path="/admin/lti-setup" element={<AdminLayoutWithNav><LTISetupPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/surveys/:surveyId/results" element={<AdminLayoutWithNav><SurveyResultsPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/credentials" element={<AdminLayoutWithNav><CredentialsDashboardPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/credential-quality" element={<AdminLayoutWithNav><CredentialDataQualityPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/assessments" element={<AdminLayoutWithNav><AssessmentsPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/assessment-reviews" element={<AdminLayoutWithNav><AssessmentReviewAdminPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/badges" element={<AdminLayoutWithNav><BadgesPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/badges/:badgeId" element={<AdminLayoutWithNav><BadgesPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/pathway-groups" element={<AdminLayoutWithNav><PathwayGroupsPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/pathways" element={<AdminLayoutWithNav><PathwaysPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/pathways/:pathwayId" element={<AdminLayoutWithNav><PathwayEditorPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/pathway-progress" element={<AdminLayoutWithNav><PathwayProgressPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/assessments/:assessmentId" element={<AdminLayoutWithNav><AssessmentEditorPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/assessments/:assessmentId/submissions" element={<AdminLayoutWithNav><AssessmentSubmissionsPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/registration-forms" element={<AdminLayoutWithNav><RegistrationFormsPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/registration-forms/:formId" element={<AdminLayoutWithNav><RegistrationFormEditorPage /></AdminLayoutWithNav>} />
                      <Route path="/admin/assessment-submissions/:submissionId" element={<AdminLayoutWithNav><AssessmentSubmissionReportPage /></AdminLayoutWithNav>} />

                      {/* Marketing Workspace SSO deep link — standalone so lesson
                          links can trigger the launch without the course shell */}
                      <Route path="/courses/:courseId/workspace" element={<WorkspaceLaunchPage />} />

                      {/* Two-column layout for specific pages */}
                      <Route element={<DashboardProvider><TwoColumnLayout /></DashboardProvider>}>
                        <Route path="/roadmaps/:templateId/instances" element={<RoadmapInstancesPage />} />
                      </Route>

                      {/* All authenticated routes under a single layout to prevent navigation bugs */}
                      <Route element={<CampaignDrawerProvider><CourseNavigationProvider><DashboardProvider><RequireActiveSeat /><CampaignDrawer /></DashboardProvider></CourseNavigationProvider></CampaignDrawerProvider>}>
                        {/* Lesson routes */}
                        <Route path="/lessons" element={<RoadmapsPage />} />
                        <Route path="/lessons/instance/:instanceId" element={<LessonViewerPage />} />

                        {/* Legacy roadmap routes — redirect to /lessons */}
                        <Route path="/roadmaps" element={<Navigate to="/lessons" replace />} />
                        <Route path="/roadmaps/instance/:instanceId" element={<LessonViewerPage />} />
                        <Route path="/roadmap" element={<RoadmapPage />} />
                        <Route path="/roadmap-instances" element={<RoadmapInstancesPage />} />
                        <Route path="/roadmap-instance" element={<RoadmapInstancePage />} />
                        <Route path="/instances" element={<InstancesPage />} />
                        <Route path="/tools-appendix" element={<ToolsAppendixPage />} />

                        {/* Knowledge Base routes (formerly Micro Learn) */}
                        <Route path="/knowledge-base" element={<KnowledgeBase />} />
                        <Route path="/knowledge-base/search" element={<KnowledgeBaseSearch />} />
                        <Route path="/knowledge-base/category/:layerSlug/:groupSlug" element={<KnowledgeBaseCategory />} />
                        <Route path="/knowledge-base/category/:layerSlug" element={<KnowledgeBaseCategory />} />
                        <Route path="/knowledge-base/:slug" element={<KnowledgeBaseArticle />} />
                        <Route path="/micro-learn" element={<Navigate to="/knowledge-base" replace />} />
                        <Route path="/micro-learn/:slug" element={<MicroLearnSlugRedirect />} />

                        {/* Profile page */}
                        <Route path="/profile" element={<ProfilePage />} />
                        <Route path="/certificates" element={<CertificatesPage />} />
                        <Route path="/certificates/:pathwaySlug" element={<CertificatesPage />} />
                        <Route path="/students" element={<StudentsPage />} />

                        <Route path="/planner/calendar" element={<PlannerCalendarPage />} />

                        {/* Instructor routes */}
                        <Route path="/student-instances/:instructorId" element={<StudentInstancesForInstructorPage />} />
                        <Route path="/instructor/course/:courseId/student/:studentId/instances" element={<StudentInstancesForInstructorPage />} />
                        <Route path="/instructor-course/:courseId" element={<InstructorCoursePage />} />

                        {/* Payment routes */}
                        <Route path="/payment/success" element={<PaymentSuccessPage />} />
                        <Route path="/payment/return" element={<PaymentReturnPage />} />
                        <Route path="/payment/cancel" element={<PaymentCancelPage />} />

                        {/* Debug routes */}
                        <Route path="/widget-demo" element={<WidgetDemoPage />} />
                        <Route path="/debug/instances" element={<UserInstanceDebug />} />
                        <Route path="/debug/brand-instances" element={<DirectBrandInstanceCheck />} />

                        {/* Dashboard and course routes */}
                        <Route path="/dashboard" element={<DashboardPage />} />
                        <Route path="/courses" element={<CoursesOverviewPage />} />
                        <Route path="/courses/:courseId" element={<CourseDetailPage />} />
                        <Route path="/courses/:courseId/roadmap/:instanceId" element={<CourseDetailPage />} />
                      </Route>

                      {/* Company-admin console - gated by RequireCompanyAdmin (not the /admin self-guards) */}
                      <Route
                        path="/company"
                        element={
                          <CompanyProvider>
                            <RequireCompanyAdmin>
                              <CompanyConsoleLayout />
                            </RequireCompanyAdmin>
                          </CompanyProvider>
                        }
                      >
                        <Route index element={<Navigate to="/company/seats" replace />} />
                        <Route path="seats" element={<CompanySeatRosterPage />} />
                        <Route path="assignments" element={<CompanyStudentAssignmentPage />} />
                        <Route path="waitlist" element={<CompanyWaitlistPage />} />
                        <Route path="instructors" element={<CompanyInstructorsPage />} />
                        <Route path="admins" element={<CompanyAdminsPage />} />
                        <Route path="settings" element={<CompanySettingsPage />} />
                        <Route path="instructor-view" element={<CompanyInstructorViewPage />} />
                        <Route path="reviewers" element={<CompanyReviewersPage />} />
                      </Route>

                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </React.Suspense>
                  <GlobalToastRenderers />
                </div>
              </Router>
              </UserflowProvider>
            </ServerStatusProvider>
          </RoadmapProvider>
        </AuthProvider>
      </WidgetProvider>
      </ModelRegistryProvider>
    </QueryClientProvider>
  );
}

export default App;
